import React, { useState } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import { ar } from 'date-fns/locale';
import Modal from '../../../components/ui/Modal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StaffAvailabilityMatrix = ({ isOpen, onClose, appointments }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { locale: ar }));

  const staff = [
    { id: 'staff001', name: 'محمد السعيد', role: 'مدير مبيعات', color: 'bg-blue-500' },
    { id: 'staff002', name: 'خالد الغامدي', role: 'مستشار عقاري', color: 'bg-green-500' },
    { id: 'staff003', name: 'أحمد السعيد', role: 'مستشار عقاري', color: 'bg-purple-500' },
    { id: 'staff004', name: 'عبدالله الشمري', role: 'مستشار عقاري', color: 'bg-orange-500' }
  ];

  const workingHours = Array.from({ length: 11 }, (_, i) => i + 8); // 8 AM to 6 PM
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const getStaffAppointments = (staffId, date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments?.filter(apt => {
      if (apt?.staffId !== staffId || apt?.date !== dateStr) return false;
      const aptHour = parseInt(apt?.time?.split(':')?.[0]);
      const aptDuration = parseInt(apt?.duration) / 60;
      return hour >= aptHour && hour < aptHour + aptDuration;
    });
  };

  const getStaffDayLoad = (staffId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments?.filter(apt => apt?.staffId === staffId && apt?.date === dateStr)?.length || 0;
  };

  const getTotalStaffLoad = (staffId) => {
    return appointments?.filter(apt => apt?.staffId === staffId)?.length || 0;
  };

  const handlePreviousWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const handleThisWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { locale: ar }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="جداول توفر الموظفين"
      icon="Users"
      iconColor="primary"
      size="full"
      footer={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-success/30" />
              <span className="text-xs text-muted-foreground">متاح</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-warning/30" />
              <span className="text-xs text-muted-foreground">مشغول جزئياً</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-error/30" />
              <span className="text-xs text-muted-foreground">مشغول</span>
            </div>
          </div>
          <Button variant="outline" onClick={onClose}>
            إغلاق
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Week Navigation */}
        <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" iconName="ChevronRight" onClick={handlePreviousWeek} />
            <Button variant="outline" size="icon" iconName="ChevronLeft" onClick={handleNextWeek} />
            <Button variant="ghost" onClick={handleThisWeek}>
              هذا الأسبوع
            </Button>
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {format(currentWeekStart, 'd MMM', { locale: ar })} - {format(addDays(currentWeekStart, 6), 'd MMM yyyy', { locale: ar })}
          </h3>
        </div>

        {/* Staff Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {staff?.map(member => {
            const totalLoad = getTotalStaffLoad(member?.id);
            return (
              <div key={member?.id} className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full ${member?.color} flex items-center justify-center text-white font-bold`}>
                    {member?.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{member?.name}</div>
                    <div className="text-xs text-muted-foreground">{member?.role}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">إجمالي المواعيد</span>
                  <span className="text-lg font-bold text-primary">{totalLoad}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Availability Matrix */}
        <div className="bg-card rounded-lg border border-border overflow-auto">
          <div className="min-w-[1000px]">
            {/* Header Row */}
            <div className="grid grid-cols-8 border-b border-border">
              <div className="p-3 bg-muted/30 font-semibold text-sm text-center">
                الموظف
              </div>
              {weekDays?.map((day, idx) => {
                const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div
                    key={idx}
                    className={`p-3 text-center border-r border-border ${
                      isToday ? 'bg-primary/10' : 'bg-muted/30'
                    }`}
                  >
                    <div className={`text-sm font-semibold ${
                      isToday ? 'text-primary' : 'text-foreground'
                    }`}>
                      {format(day, 'EEEE', { locale: ar })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {format(day, 'd MMM', { locale: ar })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Staff Rows */}
            {staff?.map(member => (
              <div key={member?.id} className="grid grid-cols-8 border-b border-border">
                <div className="p-3 bg-muted/20 flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full ${member?.color} flex items-center justify-center text-white text-xs font-bold`}>
                    {member?.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground truncate">{member?.name}</div>
                  </div>
                </div>
                {weekDays?.map((day, dayIdx) => {
                  const dayLoad = getStaffDayLoad(member?.id, day);
                  const bgColor = dayLoad === 0 ? 'bg-success/10' : 
                                 dayLoad <= 2 ? 'bg-warning/10' : 'bg-error/10';
                  
                  return (
                    <div
                      key={dayIdx}
                      className={`p-2 border-r border-border ${bgColor} hover:bg-muted/30 transition-colors`}
                    >
                      <div className="space-y-1">
                        {workingHours?.slice(0, 5)?.map(hour => {
                          const hourAppointments = getStaffAppointments(member?.id, day, hour);
                          if (hourAppointments?.length === 0) return null;
                          
                          return (
                            <div
                              key={hour}
                              className="text-xs p-1 rounded bg-primary/20 text-primary truncate"
                              title={hourAppointments?.[0]?.clientName}
                            >
                              {hour}:00
                            </div>
                          );
                        })}
                      </div>
                      {dayLoad > 0 && (
                        <div className="text-center mt-2">
                          <span className="text-xs font-bold text-primary">{dayLoad}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Alternative Time Suggestions */}
        <div className="bg-accent/10 rounded-lg border border-accent/30 p-4">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-2">
                اقتراحات الأوقات البديلة
              </h4>
              <p className="text-xs text-muted-foreground">
                عند وجود تعارض في المواعيد، سيقترح النظام تلقائياً أوقات بديلة بناءً على توفر الموظفين والعملاء
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default StaffAvailabilityMatrix;